import React, { Suspense, lazy } from 'react'
import { AuthRoute, NotFound, Route, Switch } from '@fs/zion-router'
import ErrorBoundary from '@fs/zion-error-boundary'
import { NoticeLoading } from '@fs/zion-icon'
import { RecordAuthRoute } from '@fs/zion-gedcomx'
import Cookies from 'js-cookie'
import NavigationTabs from './components/search/NavigationTabs'

/**
 * This will retry failed chunks up to 5 times
 * Hopefully it will decrease Sentry errors
 * @param {Function} lazyComponent - lazy component import function
 * @param {Number} attemptsLeft - number of attempts to make (defaults to 5)
 * @returns {Promise<any>} Promise that rejects if all additional attempts fail.
 */
function componentLoader(lazyComponent, attemptsLeft = 5) {
  return new Promise((resolve, reject) => {
    lazyComponent()
      .then(resolve)
      .catch((error) => {
        // let us retry after 1500 ms
        setTimeout(() => {
          if (attemptsLeft === 1) {
            reject(error)
            return
          }
          componentLoader(lazyComponent, attemptsLeft - 1).then(resolve, reject)
        }, 1500)
      })
  })
}

// Dynamically load components to reduce bundle size
// https://reactjs.org/docs/react-api.html#reactlazy
const CatalogDetails = lazy(() => componentLoader(() => import('./components/catalog/CatalogDetailsPage')))
const CollectionDetails = lazy(() =>
  componentLoader(() => import('./components/collection-details/CollectionDetailsPage'))
)
const FilmDebugPage = lazy(() => componentLoader(() => import('./components/film-debug/FilmDebugPage')))
const GenSearchPage = lazy(() => componentLoader(() => import('./components/gen/GenSearchPage')))
const GenResultsPage = lazy(() => componentLoader(() => import('./components/gen/GenResultsPage')))
const HRComparePage = lazy(() => componentLoader(() => import('./components/hr/HRComparePage')))
const LocationListPage = lazy(() => componentLoader(() => import('./components/location/LocationListPage')))
const RecordDetailsPage = lazy(() => componentLoader(() => import('./components/record-details/RecordDetailsPage')))
const RecordLoadingSkeleton = lazy(() => componentLoader(() => import('./components/record-details/LoadingSkeleton')))
const SearchHomePage = lazy(() => componentLoader(() => import('./components/search/SearchHomePage')))
const SearchResultsPage = lazy(() => componentLoader(() => import('./components/search/SearchResultsPage')))

// The search-react code thinks it operates on /search so when the router tries to unwrap a history item
//  that came from /search (artifact or bifrost) it thinks it owns /search so it doesn't bother to load
//  the page from artifact or bifrost. Instead it returns a 404 (<NotFound>). We are wrapping the <NotFound>
//  with some logic to force a page reload to happen which should solve the problem. We are also storing a
//  cookie to make sure we don't end up in an endless loop of reloads. This is why we use CustomXXX elements.
const NOT_FOUND_STORAGE_KEY = 'app.search.notfound.href'

function clearNotFoundKey() {
  Cookies.remove(NOT_FOUND_STORAGE_KEY)
}

function CustomNotFound() {
  if (
    window.location.pathname.indexOf('/search') === 0 &&
    Cookies.get(NOT_FOUND_STORAGE_KEY) !== window.location.href
  ) {
    Cookies.set(NOT_FOUND_STORAGE_KEY, window.location.href)
    window.location.reload(true)
    return null
  }
  return <NotFound />
}

function CustomRoute({ children, path, exact, component, requirePersonalData }) {
  clearNotFoundKey()
  return (
    <Route path={path} exact={exact} component={component} requirePersonalData={requirePersonalData}>
      {children}
    </Route>
  )
}

function CustomAuthRoute({ children, path, exact, component, requirePersonalData, LoadingSkeleton }) {
  clearNotFoundKey()
  return (
    <AuthRoute
      path={path}
      exact={exact}
      component={component}
      requirePersonalData={requirePersonalData}
      LoadingSkeleton={LoadingSkeleton}
    >
      {children}
    </AuthRoute>
  )
}

function CustomRecordAuthRoute({ children, path, component, LoadingSkeleton }) {
  clearNotFoundKey()
  return (
    <RecordAuthRoute path={path} component={component} LoadingSkeleton={LoadingSkeleton}>
      {children}
    </RecordAuthRoute>
  )
}

const EMBEDDED_MOBILE_COOKIE_NAME = 'embeddedMobile'

function App() {
  // Hide NavigationTabs if page is embedded in mobile app WebView
  const embeddedMobile = !!Cookies.get(EMBEDDED_MOBILE_COOKIE_NAME)

  return (
    <ErrorBoundary>
      <Suspense fallback={<NoticeLoading />}>
        {!embeddedMobile && <NavigationTabs />}

        <Switch>
          <CustomRoute path="/" exact component={SearchHomePage} />
          <CustomRecordAuthRoute
            path="/ark\:/61903/1\:1\::arkPath"
            component={RecordDetailsPage}
            LoadingSkeleton={<RecordLoadingSkeleton />}
          />

          <CustomRoute path="/catalog(/oclc)?/:catId" component={CatalogDetails} />
          <CustomRoute path="/collection/:collectionId" component={CollectionDetails} />
          <CustomRoute path="/family-trees" exact component={GenSearchPage} requirePersonalData />
          <CustomRoute path="/family-trees/results" component={GenResultsPage} requirePersonalData />
          <CustomRoute path="/filmdebug" component={FilmDebugPage} />
          <CustomRoute path="/hrcompare" component={HRComparePage} />
          <CustomRoute path="/location/list" component={LocationListPage} />
          <CustomAuthRoute path="/results" component={SearchResultsPage} />
          <CustomRoute path="/blank" component={() => 'This page is intentionally left blank'} />
          <Route component={CustomNotFound} />
        </Switch>
      </Suspense>
    </ErrorBoundary>
  )
}
export default App
